import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiServerUrl } from '../config.json';
import join from 'url-join';

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const PENDING_VERSION_KEY = 'pendingVersion';
const LAST_RELOAD_VERSION_KEY = 'lastReloadVersion';
const MAX_RELOADS = 3; // Only allow 3 reloads per detected version
const RELOAD_COOLDOWN = 10 * 60 * 1000; // 10 minutes cooldown after a reload

/**
 * Compares two version strings like "3.0.0.0" and "2.11.0.9".
 * Returns:
 *   1 if a > b
 *  -1 if a < b
 *   0 if a === b
 */
const compareVersions = (a, b) => {
    const partsA = a.split('.').map(Number);
    const partsB = b.split('.').map(Number);
    const length = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < length; i++) {
        const numA = partsA[i] || 0;
        const numB = partsB[i] || 0;
        if (numA > numB) return 1;
        if (numA < numB) return -1;
    }
    return 0;
};

/**
 * Check if we should allow a reload.
 * Prevents reload loops by tracking:
 * 1. Which version we already reloaded for
 * 2. How many times we've reloaded
 * 3. A time-based cooldown
 */
const canReload = (targetVersion) => {
    try {
        const stored = localStorage.getItem(LAST_RELOAD_VERSION_KEY);
        if (!stored) return true;

        const { version, count, timestamp } = JSON.parse(stored);

        // If cooldown has passed, allow reload again
        if (Date.now() - timestamp > RELOAD_COOLDOWN) return true;

        // If it's a different (even newer) version, reset and allow
        if (version !== targetVersion) return true;

        // Same version within cooldown — check count
        return count < MAX_RELOADS;
    } catch {
        return true;
    }
};

const recordReload = (targetVersion) => {
    try {
        const stored = localStorage.getItem(LAST_RELOAD_VERSION_KEY);
        let count = 1;

        if (stored) {
            const parsed = JSON.parse(stored);
            // Only increment if same version AND within cooldown
            if (parsed.version === targetVersion && Date.now() - parsed.timestamp <= RELOAD_COOLDOWN) {
                count = (parsed.count || 0) + 1;
            }
        }

        localStorage.setItem(
            LAST_RELOAD_VERSION_KEY,
            JSON.stringify({
                version: targetVersion,
                count,
                timestamp: Date.now(),
            })
        );
    } catch {
        // localStorage not available, proceed anyway
    }
};

/**
 * Check if there is a pending version update stored in localStorage.
 * If yes and reload is allowed, trigger a page reload.
 */
export const triggerReloadIfNeeded = () => {
    try {
        const pendingVersion = localStorage.getItem(PENDING_VERSION_KEY);
        if (!pendingVersion) return;

        if (!canReload(pendingVersion)) {
            console.log(
                `[VersionCheck] Pending version ${pendingVersion} but reload skipped to prevent loop.`
            );
            return;
        }

        console.log(`[VersionCheck] Reloading for pending version: ${pendingVersion}`);
        recordReload(pendingVersion);
        localStorage.removeItem(PENDING_VERSION_KEY);
        window.location.reload();
    } catch {
        // fail silently
    }
};

/**
 * 更新流程:
 * 1. 定期从后端获取最新版本号，与本地 build-version.json 比较
 * 2. 如果后端版本更新，记录到 localStorage（不立即刷新）
 * 3. 当路由变化时，检测到有 pending version update，触发 reload
 * 4. 当接口报错时，也会检测并触发 reload（通过 axios interceptor 调用 triggerReloadIfNeeded）
 */
export const useVersionCheck = () => {
    const currentBuildVersionRef = useRef(null);
    const isCheckingRef = useRef(false);
    const location = useLocation();

    // 获取当前前端构建版本，并尽量绕过浏览器和中间层缓存。
    const fetchLocalBuildVersion = useCallback(async () => {
        try {
            const response = await fetch('/build-version.json?t=' + Date.now(), {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                },
            });
            if (!response.ok) return null;
            const data = await response.json();
            return data.version || null;
        } catch {
            return null;
        }
    }, []);

    // 获取后端发布版本号
    const fetchBackendVersion = useCallback(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
            const response = await fetch(join(ApiServerUrl, '/api/sse-endpoint/'), {
                credentials: 'include',
                headers: { Accept: 'text/event-stream' },
                signal: controller.signal,
            });
            if (!response.ok) {
                clearTimeout(timeoutId);
                return null;
            }

            // Read the stream chunk by chunk since SSE connections don't close
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let text = '';

            while (true) {
                const { done, value } = await reader.read();
                text += decoder.decode(value || new Uint8Array(), { stream: !done });

                // Once we have the data line, we can stop reading
                const dataMatch = text.match(/data:\s*(.+)/);
                if (dataMatch) {
                    clearTimeout(timeoutId);
                    reader.cancel();
                    try {
                        return JSON.parse(dataMatch[1].trim());
                    } catch {
                        return dataMatch[1].trim().replace(/^"|"$/g, '');
                    }
                }

                if (done) break;
            }

            clearTimeout(timeoutId);
            return null;
        } catch {
            clearTimeout(timeoutId);
            return null;
        }
    }, []);

    // 定期检查后端版本，如果有新版本则记录到 localStorage
    useEffect(() => {
        let intervalId = null;

        const checkVersion = async () => {
            if (isCheckingRef.current) return;
            isCheckingRef.current = true;

            try {
                // On first check, capture the local build version
                if (!currentBuildVersionRef.current) {
                    const localVersion = await fetchLocalBuildVersion();
                    if (!localVersion) return;
                    currentBuildVersionRef.current = localVersion;
                    console.log(`[VersionCheck] Current build version: ${localVersion}`);
                }

                // Fetch the latest version from backend
                const backendVersion = await fetchBackendVersion();
                if (!backendVersion) return;

                // Compare: if backend version is newer, store as pending (don't reload yet)
                const comparison = compareVersions(backendVersion, currentBuildVersionRef.current);
                if (comparison > 0) {
                    const existingPending = localStorage.getItem(PENDING_VERSION_KEY);
                    if (existingPending !== backendVersion) {
                        console.log(
                            `[VersionCheck] New version detected: ${backendVersion} (current: ${currentBuildVersionRef.current}). Stored as pending.`
                        );
                        localStorage.setItem(PENDING_VERSION_KEY, backendVersion);
                    }
                }
            } finally {
                isCheckingRef.current = false;
            }
        };

        // Also check when the tab becomes visible again
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };

        // Initial check
        checkVersion();

        // Periodic polling
        intervalId = setInterval(checkVersion, CHECK_INTERVAL);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalId) clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchLocalBuildVersion, fetchBackendVersion]);

    // 路由变化时，检测是否有 pending version update，有则触发 reload
    useEffect(() => {
        triggerReloadIfNeeded();
    }, [location.pathname]);
};
